import { t } from '@if/core/i18n'
import { AnyError, getErrorMessage } from '@if/mui'
import { useSystemFlows } from '@src/api/app-config-hooks'
import { useAcquireLock, useGetLock, useReleaseLock } from '@src/api/loan/loan-api'
import { LoanLockDto } from '@src/api/loan/loan-dto'
import { useLoanCalendarSimulation } from '@src/components/LoanCalendar/LoanCalendarSimulationProvider'
import { useNotification } from '@src/providers/NotificationProvider'
import { useUser } from '@src/providers/UserProvider'
import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react'

interface LoanLockState {
  lock: LoanLockDto | null
  isLocked: boolean
  isLockedByOther: boolean
  isLockedBySystem: boolean
  isReadOnly: boolean
  isLoading: boolean
}

interface LoanLockContextValue extends LoanLockState {
  acquireLock: () => Promise<void>
  releaseLock: () => Promise<void>
}

const LoanLockContext = createContext<LoanLockContextValue | null>(null)

export const useLoanLock = () => {
  const context = useContext(LoanLockContext)
  if (!context) {
    throw new Error('useLoanLock must be used within a LoanLockProvider')
  }
  return context
}

interface LoanLockProviderProps {
  children: ReactNode
}

const LoanLockProvider = ({ children }: LoanLockProviderProps) => {
  const { originalSimulation } = useLoanCalendarSimulation()
  const loanId = originalSimulation.simulation.fullLoan.loan.id
  const financingProgram = originalSimulation.simulation.fullLoan.loan.financingProgramId
  const { showSuccess, showError } = useNotification()
  const currentUser = useUser()
  const systemFlows = useSystemFlows()

  const isLockedBySystem = useMemo(() => {
    if (!systemFlows?.financingProgramConfig?.[financingProgram]) return false
    return !systemFlows.hasWriteAccess(financingProgram)
  }, [systemFlows, financingProgram])

  const [lock, setLock] = useState<LoanLockDto | null>(null)
  const [isLoading, setIsLoading] = useState(false)

  const { mutateAsync: acquireLockMutation } = useAcquireLock(loanId, financingProgram)
  const { mutateAsync: releaseLockMutation } = useReleaseLock(loanId, financingProgram)
  const { data: initialLockData } = useGetLock(loanId, financingProgram, !isLockedBySystem)

  // Set initial lock state when data is loaded
  useEffect(() => {
    if (initialLockData) {
      setLock(initialLockData)
    }
  }, [initialLockData])

  const isLocked = useMemo(() => {
    return lock?.isAcquired === true && lock.userId === currentUser?.id
  }, [lock, currentUser?.id])

  const isLockedByOther = useMemo(() => {
    return lock?.isAcquired === true && lock?.userId !== null && lock?.userId !== undefined && lock.userId !== currentUser?.id
  }, [lock, currentUser?.id])

  const isReadOnly = useMemo(() => {
    return isLockedByOther || isLockedBySystem
  }, [isLockedByOther, isLockedBySystem])

  const acquireLock = useCallback(async () => {
    if (isLockedBySystem) return
    setIsLoading(true)
    try {
      const result = await acquireLockMutation()

      if (result.isAcquired) {
        setLock(result)
        showSuccess(t('common.lockSuccess.acquire'))
      } else {
        // Update the lock state to reflect who currently holds it
        setLock(result)

        const expiresOn = result.expiresOn ? new Date(result.expiresOn).toLocaleString() : 'unknown'
        showError(
          t('common.lockError.failedAcquire', {
            username: result.username || 'Unknown user',
            expiresOn,
          }),
        )
      }
    } catch (error: unknown) {
      showError(getErrorMessage(error as AnyError))
    } finally {
      setIsLoading(false)
    }
  }, [isLockedBySystem, acquireLockMutation, showSuccess, showError])

  const releaseLock = useCallback(async () => {
    if (isLockedBySystem) return
    setIsLoading(true)
    try {
      const result = await releaseLockMutation()

      if (!result.isAcquired) {
        setLock(result)
        showSuccess(t('common.lockSuccess.release'))
      } else {
        // Update the lock state to reflect who currently holds it
        setLock(result)

        const expiresOn = result.expiresOn ? new Date(result.expiresOn).toLocaleString() : 'unknown'
        showError(
          t('common.lockError.failedRelease', {
            username: result.username || 'Unknown user',
            expiresOn,
          }),
        )
      }
    } catch (error: unknown) {
      showError(getErrorMessage(error as AnyError))
    } finally {
      setIsLoading(false)
    }
  }, [isLockedBySystem, releaseLockMutation, showSuccess, showError])

  const contextValue = useMemo<LoanLockContextValue>(
    () => ({
      lock,
      isLocked,
      isLockedByOther,
      isLockedBySystem,
      isReadOnly,
      isLoading,
      acquireLock,
      releaseLock,
    }),
    [lock, isLocked, isLockedByOther, isLockedBySystem, isReadOnly, isLoading, acquireLock, releaseLock],
  )

  return <LoanLockContext.Provider value={contextValue}>{children}</LoanLockContext.Provider>
}

export default LoanLockProvider
